import {
    ApartmentBuildingView,
    ApartmentBuildingViewPointer,
    ExtendedApartmentBuilding,
    getApartmentBuildingView,
} from '@bpd-library/utilities';
import { VirtualTourMinimap, VirtualTourPointer, VirtualView } from '@features/virtual-tour/types';
import { formatConstructionNumberStatus, formatDetailPageUrl } from '@utilities/api-formatters';
import { IMAGE_PLACEHOLDER } from '@utilities/constants';
import { getPrefixedUrl } from '@utilities/get-prefixed-url';
import { useAreaData, useAreaDataUnsafe } from '@utilities/hooks';

export const getApartmentBuilding = async (options: {
    constructionNumberId?: string | null;
    viewId?: string | null;
}) => {
    let apartmentBuilding: ExtendedApartmentBuilding | undefined;

    const { constructionNumbers } = await useAreaData();

    if (options.constructionNumberId) {
        const constructionNumber = constructionNumbers.find(
            (c) => c.id === options.constructionNumberId,
        );
        apartmentBuilding = constructionNumber?.apartmentBuilding;
    } else if (options.viewId) {
        apartmentBuilding = getApartmentBuildingByViewId(options.viewId);
    }

    return apartmentBuilding;
};

export const getViewIdByConstructionNumberId = async (constructionNumberId: string) => {
    const { constructionNumbers } = await useAreaData();

    const constructionNumber = constructionNumbers.find((c) => c.id === constructionNumberId);
    return (constructionNumber && getApartmentBuildingView(constructionNumber)?.id) || null;
};

export const transformApartmentBuildingViews = (apartmentBuilding?: ExtendedApartmentBuilding) => {
    const minimap = transformMinimap(apartmentBuilding?.views?.find((v) => v.type === 'minimap'));
    const views = (
        minimap
            ? apartmentBuilding?.views?.filter((v) => v.type !== 'minimap')
            : apartmentBuilding?.views
    )?.map<VirtualView>(mapViewFor(apartmentBuilding));
    return { minimap, views };
};

const transformMinimap = (minimapView?: ApartmentBuildingView): VirtualTourMinimap | undefined => {
    return (
        minimapView && {
            id: minimapView.id!,
            source: minimapView.imageUrl!,
            pointers: minimapView.pointers!.filter(isPointerDataClean).map(mapPointer),
        }
    );
};

function mapViewFor(apartmentBuilding?: ExtendedApartmentBuilding) {
    return (abView: ApartmentBuildingView): VirtualView => {
        if (abView.imageUrl) {
            return {
                type: '2d',
                id: abView.id!,
                source: getPrefixedUrl(abView.imageUrl) || IMAGE_PLACEHOLDER,
                pointers: abView.pointers!.filter(isPointerDataClean).map(mapPointer),
                ...(apartmentBuilding && { apartmentBuildingId: apartmentBuilding.id }),
            };
        } else {
            return {
                type: '3d',
                id: abView.id!,
                source: abView.sourceUrl!,
                ...(apartmentBuilding && { apartmentBuildingId: apartmentBuilding.id }),
            };
        }
    };
}

function isPointerDataClean(pointer: ApartmentBuildingViewPointer): boolean {
    const { constructionNumbers } = useAreaDataUnsafe() ?? {};

    if (pointer.constructionNumberId) {
        const constructionNumber = constructionNumbers?.find(
            (cn) => cn.id === pointer.constructionNumberId,
        );

        if (!constructionNumber) return false;
    }

    return true;
}

function mapPointer(pointer: ApartmentBuildingViewPointer): VirtualTourPointer {
    const { coordinates, compassDirection, label, type, constructionNumberId, linkToViewId } =
        pointer;
    if (!coordinates) {
        console.warn('[virtual-tour]: rendering pointer without coordinates: >>> ', pointer);
        return {} as VirtualTourPointer;
    }

    const { constructionNumbers } = useAreaDataUnsafe() ?? {};

    if (type === 'label') {
        const constructionNumber = constructionNumbers?.find(
            (cn) => cn.id === constructionNumberId,
        );

        return {
            type: 'label',
            position: [coordinates[0], coordinates[1]],
            constructionNumber: {
                id: constructionNumberId,
                identifier: label,
                status: formatConstructionNumberStatus(constructionNumber!),
                url: getPrefixedUrl(
                    formatDetailPageUrl({ constructionNumber: constructionNumber! }) || '',
                ),
            },
        };
    } else if (type === 'text') {
        return {
            type: 'text',
            position: [coordinates[0], coordinates[1]],
            label,
        };
    } else if (type === 'pointer') {
        const linkedView = getApartmentBuildingByViewId(linkToViewId)?.views?.find(
            (v) => v.id === linkToViewId,
        );
        const amount = linkedView?.pointers?.filter((p) => p.constructionNumberId).length || 0;
        return {
            type: amount ? 'number' : 'generic',
            position: [coordinates[0], coordinates[1]],
            sceneId: linkToViewId,
            amount,
            label,
        };
    } else {
        return {
            type: pointerTypeTransform(type),
            position: [coordinates[0], coordinates[1]],
            eyeDirection: compassDirection,
            sceneId: linkToViewId,
            label,
        };
    }
}

function pointerTypeTransform(type?: string | null, compassDirection?: string | null) {
    switch (type) {
        case 'compass':
            return `eye-${compassDirection}`;
        default:
            return type || 'generic';
    }
}

function getApartmentBuildingByViewId(
    viewId?: string | null,
): ExtendedApartmentBuilding | undefined {
    const { apartmentBuildings } = useAreaDataUnsafe() || {};

    if (!apartmentBuildings || !viewId) return;
    return apartmentBuildings?.find((apartmentBuilding) =>
        apartmentBuilding.views?.find((view) => view.id === viewId),
    );
}
